import React, { useRef, useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Resize/re-encode client-side so large phone photos survive server body limits.
const compressImage = async (file) => {
  try {
    const bmp = await createImageBitmap(file);
    const MAX = 2560;
    const scale = Math.min(1, MAX / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch { return file; } // e.g. HEIC decode unsupported -> upload original
};

export function DealPhotosCard({ projectId, photos = [], isAcMember, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [viewer, setViewer] = useState(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress(files.length > 1 ? `${i + 1}/${files.length}` : '');
      try {
        const f = await compressImage(files[i]);
        await api.uploadPhotos(projectId, [f]);
        ok++;
      } catch (err) {
        const status = err.response?.status;
        const d = err.response?.data?.detail;
        toast.error(typeof d === 'string' ? d
          : status === 413 ? `"${files[i].name}" is too large for the server — please try a smaller photo`
            : `Upload of "${files[i].name}" failed${status ? ` (HTTP ${status})` : ' — check your connection'}`);
      }
    }
    if (ok) {
      toast.success(`${ok} photo${ok > 1 ? 's' : ''} uploaded`);
      onChanged();
    }
    setUploading(false);
    setProgress('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = async (photoId) => {
    try { await api.deletePhoto(projectId, photoId); toast.success('Photo deleted'); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const url = (p) => `${API_BASE}/api/projects/${projectId}/photos/${p.id}/file`;

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="deal-photos-card">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading text-base text-foreground flex items-center gap-2">
          <Camera className="w-4 h-4 text-foreground/50" /> Photos ({photos.length})
        </p>
        {!isAcMember && (
          <>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="photo-upload-input" />
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}
              data-testid="photo-upload-btn"
              className="border-blue-600/40 text-blue-400 hover:bg-blue-600/10 rounded-sm text-[11px] h-7 px-2.5">
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
              {uploading && progress ? `Uploading ${progress}…` : 'Add Photos'}
            </Button>
          </>
        )}
      </div>
      {photos.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No photos yet — upload pictures from the on-site visit here. They unlock the move to &ldquo;On-Site&rdquo;.
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {photos.map((p, i) => (
            <div key={p.id} className="relative group aspect-square rounded-sm overflow-hidden border border-border bg-secondary/30">
              <img src={url(p)} alt={p.filename} loading="lazy" data-testid={`deal-photo-${i}`}
                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setViewer(p)} />
              {!isAcMember && (
                <button onClick={() => remove(p.id)} data-testid={`delete-photo-${i}`}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-red-900/80 text-white rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {viewer && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6" onClick={() => setViewer(null)} data-testid="photo-viewer">
          <button className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
          <img src={url(viewer)} alt={viewer.filename} className="max-w-full max-h-full rounded-sm object-contain" />
          <p className="absolute bottom-4 text-[11px] text-white/60 font-mono">
            {viewer.filename} · {viewer.uploaded_by} · {(viewer.uploaded_at || '').slice(0, 10)}
          </p>
        </div>
      )}
    </div>
  );
}
